import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Divider,
  Paper,
  Grid,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
} from "@mui/icons-material";
import MDButton from "shared/components/MDButton";
import MDBox from "shared/components/MDBox";
import logoImage from "../../../../../assets/images/logo horinzontal.png";

const SuccessDialog = ({ open, onClose, userData, autoClose = true, autoCloseDelay = 3000 }) => {
  const [countdown, setCountdown] = useState(autoCloseDelay / 1000);

  useEffect(() => {
    if (open && autoClose) {
      setCountdown(autoCloseDelay / 1000);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            onClose();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [open, autoClose, autoCloseDelay, onClose]);

  // Formatear fecha
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const getVehicleType = (id) => {
    return id === 1 ? "Vehículo" : "Moto";
  };

  if (!userData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header con gradiente verde */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #09702d 0%, #0a8511 100%)",
          p: 3,
          pb: 4,
          textAlign: "center",
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "rgba(255,255,255,0.7)",
            "&:hover": { color: "white" },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Logo */}
        <Box
          component="img"
          src={logoImage}
          alt="GoPass"
          sx={{
            height: 50,
            width: "auto",
            objectFit: "contain",
            mb: 2,
            filter: "brightness(0) invert(1)",
          }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />

        {/* Icono de éxito */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Box
            sx={{
              backgroundColor: "rgba(255,255,255,0.2)",
              borderRadius: "50%",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 64, color: "white" }} />
          </Box>
        </Box>

        <Typography variant="h5" fontWeight="bold" color="rgba(255,255,255,0.85)" sx={{ mt: 2 }}>
          ¡Usuario Creado Exitosamente!
        </Typography>
        <Typography variant="body2" color="rgba(255,255,255,0.85)">
          El usuario ha sido registrado en el sistema
        </Typography>
      </Box>

      {/* Contenido */}
      <DialogContent sx={{ pt: 3, px: 3, pb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "12px",
            border: "1px solid #e8f5e9",
            backgroundColor: "#f1f8e9",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <PersonIcon sx={{ color: "#09702d" }} />
                <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                  DATOS DEL USUARIO
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Placa
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {userData.plate || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Tipo
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {getVehicleType(userData.idvehicletype)}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Nombre
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {userData.name || "N/A"} {userData.lastname || ""}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Cédula
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {userData.cedula || "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Residencial
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {userData.residential || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Creado Por
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {userData.UserGoper || "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Fecha Inicio
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatDate(userData.start_date)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Fecha Fin
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatDate(userData.end_date)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Contador de cierre automático */}
        {autoClose && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">
              Cerrando en {countdown} segundos...
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <MDButton
            variant="contained"
            color="primary"
            onClick={onClose}
            startIcon={<CheckCircleIcon />}
            sx={{
              borderRadius: "8px",
              fontWeight: "bold",
              px: 4,
              height: "48px",
            }}
          >
            Aceptar
          </MDButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

SuccessDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userData: PropTypes.object,
  autoClose: PropTypes.bool,
  autoCloseDelay: PropTypes.number,
};

SuccessDialog.defaultProps = {
  userData: null,
  autoClose: true,
  autoCloseDelay: 3000,
};

export default SuccessDialog;
